import { useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export const REPORT_REASONS = [
  ["child_safety", "Puts a child at risk"],
  ["bullying", "Bullying or harassment"],
  ["inappropriate", "Inappropriate or adult content"],
  ["scam", "Scam or fraud"],
  ["unsafe_item", "Unsafe or prohibited item"],
  ["spam", "Spam"],
  ["other", "Something else"],
];

// target: { type: "post" | "listing" | "message", id, threadId? }
export default function ReportDialog({ target, onClose }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setReason(""); setDetails(""); onClose(); };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reports", { target_type: target.type, target_id: target.id, thread_id: target.threadId, reason, details });
      toast.success("Thanks — our safety team will review this.");
      close();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send report"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Flag className="h-5 w-5 text-rose-500" /> Report this {target?.type}</DialogTitle>
          <DialogDescription>Reports are private. The person won't know it was you.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 mt-2" data-testid="report-reasons">
          {REPORT_REASONS.map(([value, label]) => (
            <button key={value} type="button" onClick={() => setReason(value)} data-testid={`report-reason-${value}`}
              className={`text-left rounded-2xl border-2 px-4 py-2.5 text-sm font-semibold transition-colors ${reason === value ? "border-rose-400 bg-rose-50 text-rose-700" : "border-slate-100 hover:border-slate-200 text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
        <Textarea data-testid="report-details" value={details} onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
          placeholder="Anything else we should know? (optional)" className="rounded-xl mt-2" />
        <Button onClick={submit} disabled={!reason || busy} data-testid="report-submit-btn"
          className="w-full h-11 rounded-xl font-bold bg-rose-500 hover:bg-rose-600 mt-2">
          {busy ? "Sending…" : "Send report"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
